import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Handle from '~/prix/react/components/handle'
import Modal from '~/prix/react/components/modal'
import useAPI from '~/prix/react/hooks/api'
import AskPrixAiEmptyState from '~/components/llm/askPrixAiEmptyState'
import buildVariableSearchQuestion from '~/components/llm/buildVariableSearchQuestion'
import useAskPrixAi from '~/components/llm/hooks/useAskPrixAi'
import VariableSearchMatchBanner from '~/components/llm/variableSearchMatchBanner'
import recommendedLayerForVariable from '~/packages/general/variable/recommendedLayerForVariable.util'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import { Variable, VariableWithConclusion } from '~/packages/general/variable/variables.typing'

import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import useAction from '~/prix/react/hooks/action'
import variableListDefinition from '~/packages/general/variable/list/variableList.definition'
import useDebounce from 'react-use/lib/useDebounce'

import { NotFoundInfo } from './styles/variablesView'
import categories from '../categories'
import InfiniteScroll from './viewSelectModalVariable.scroll'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList } from '@fortawesome/free-solid-svg-icons'
import WrapperFilters from './viewSelectModalVariable.filters'
import { useMedia } from 'react-use'
import { Expression } from '~/prix/types/expression'
import { BaseWrapper } from './styles/baseModalWrapper'
import chroma from 'chroma-js'
import { hasAnyUserRole } from '~/utils/shared/userProfileCompatibility'
import { FilterableByShape } from './filterableByField.util'
import { useLocation, useNavigate } from 'react-router'
import { isVariableLayer } from './clusterListInput.util'

const WrapperModalVariable = styled(BaseWrapper)`
  .content {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
  }
  .filters {
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .search {
    flex: 0 0 auto;
    position: relative;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-top-right-radius: 8px;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      border-top-left-radius: 8px;
      border-bottom-right-radius: 8px;
      width: 85%;
    }

    input {
      background-color: transparent;
      width: 100%;
      padding: 1rem;
      border: none;
      outline: none;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        border-bottom: 0;
      }
    }
  }

  .filter-category {
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      width: 15%;
      border-radius: 8px;
      justify-content: center;
      align-items: center;
    }
  }
`

const Variables = styled(BaseWrapper)`
  flex: 1;
  width: 100%;

  .variable {
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: space-between;
    gap: 3px;
    padding: 1rem 1rem;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      margin-left: 1rem;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
    }

    &:hover {
      background-color: ${({ theme }) => chroma(theme.colors.lightGrey).darken(0.15).hex()};
    }

    &.is-selected,
    &.is-selected:hover {
      background-color: ${({ theme }) => theme.colors.lightGrey};
    }

    .alert {
      padding: 0.3rem 1rem;
      border-radius: 8px;
      margin-right: 0.3rem;
      text-transform: uppercase;
      font-weight: bold;
      white-space: nowrap;
    }

    .button {
      user-select: none;
      cursor: pointer;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      padding: 0.3rem 1rem;
      border-radius: 8px;
      margin-right: 0.3rem;
      border: none;
      white-space: nowrap;
    }
  }
`

const ButtonFilter = styled.button`
  display: flex;
  flex: 1;
  background: transparent;
  box-shadow: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  color: #0c2340;
  margin: 0.2rem;
  height: 95%;
  border-radius: 8px;
`
interface ViewSelectModalVariableProps {
  variables: VariableWithConclusion[]
  isOpen: boolean
  onClose: () => void
  layer: Layer
  baseLayer: Layer
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  stateId: number | null
  stateName: string | null
}

function isPropertyExpression(
  expression: Expression,
): expression is Extract<Expression, { kind: 'property' }> {
  return expression.kind === 'property'
}

function combineExpressions(
  expressions: Expression[],
  operator: 'AND' | 'OR',
): Expression | undefined {
  const [firstExpression, ...remainingExpressions] = expressions
  if (!firstExpression) {
    return undefined
  }

  return remainingExpressions.reduce<Expression>(
    (combinedExpression, expression) => ({
      type: 'expression',
      kind: 'binary',
      left: combinedExpression,
      operator,
      right: expression,
    }),
    firstExpression,
  )
}

function getFilterableByShape(
  filterableBy: Variable['filterableBy'],
): FilterableByShape | undefined {
  if (!filterableBy || typeof filterableBy !== 'object' || !('shape' in filterableBy)) {
    return undefined
  }

  const { shape } = filterableBy
  if (!shape || typeof shape !== 'object') {
    return undefined
  }

  return shape as FilterableByShape
}

export default function ViewSelectModalVariable({
  variables,
  isOpen,
  onClose,
  layer,
  baseLayer,
  onChangeLayer,
  stateId,
  stateName,
}: ViewSelectModalVariableProps) {
  const { context, isLoading: isLoadingAPI } = useAPI()
  const preferredGeneralSettings = context.user?.activeClient?.settings?.general
  const navigate = useNavigate()
  const location = useLocation()
  const { askPrixAi, isLoading: isOpeningPrixAi } = useAskPrixAi()
  const [currentBaseLayer, setCurrentBaseLayer] = useState<Layer>(baseLayer)
  const [currentCategory, setCurrentCategory] = useState<typeof categories>({
    all: { title: 'Tudo', sectorKey: 'general' },
  })
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const isDebouncing = search !== debouncedSearch && search !== '' && debouncedSearch !== ''
  const [collapsedStates, setCollapsedStates] = useState<{ [key: string]: boolean }>({})
  const [variablesPaginated, setVariablesPaginated] = useState<VariableWithConclusion[]>([])
  const [indexes, setIndexes] = useState({ startIndex: 0, endIndex: 50 })
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasChangedVariableLayer, setHasChangedVariableLayer] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [filters, setFilters] = useState<Expression[] | undefined>([])
  const [filterableBy, setFilterableBy] = useState<FilterableByShape | undefined>()
  const filterRef = useRef<Expression[] | undefined>([])
  const isMobile = useMedia('(max-width: 768px)')

  useEffect(() => {
    setCurrentBaseLayer(baseLayer)
  }, [layer])

  useDebounce(
    () => {
      if (!search) {
        if (debouncedSearch) {
          setVariablesPaginated([])
          setHasChangedVariableLayer(true)
        }
      }
      setDebouncedSearch(search)
      setIndexes({ startIndex: 0, endIndex: 50 })
    },
    1500,
    [search],
  )
  const categoryKey = currentCategory && Object.keys(currentCategory)[0]

  const filteredVariablesResult = useAction(
    variableListDefinition,
    {
      search: debouncedSearch,
      category: categoryKey,
      startIndex: indexes.startIndex,
      endIndex: indexes.endIndex,
      preferredSpaceDelimitationType: preferredGeneralSettings?.preferredSpaceDelimitationType,
      preferredSpaceDelimitationId: preferredGeneralSettings?.preferredSpaceDelimitationId,
    },
    [
      debouncedSearch,
      indexes.startIndex,
      indexes.endIndex,
      categoryKey,
      preferredGeneralSettings?.preferredSpaceDelimitationType,
      preferredGeneralSettings?.preferredSpaceDelimitationId,
    ],
    {
      autoLoad: (Boolean(context.user) && !isLoadingAPI) || isDebouncing,
      staleWhileRevalidate: false,
    },
  )

  useEffect(() => {
    const result = filteredVariablesResult.result
    if (!result) return

    const items = result.items ?? []

    setVariablesPaginated(prev => {
      if (indexes.startIndex === 0) {
        return items
      }

      return [...prev, ...items]
    })

    setIsLoadingMore(false)
    setHasChangedVariableLayer(false)
  }, [filteredVariablesResult.result])

  const getFilter = useCallback((expressions: Record<number, Expression> | undefined) => {
    filterRef.current = expressions ? Object.values(expressions) : []
    setFilters(filterRef.current)
  }, [])

  const hasPath = 'path' in currentBaseLayer.variable && Boolean(currentBaseLayer.variable.path)

  const handleSelectVariable = useCallback(
    (variable: VariableWithConclusion) => {
      if (!context.user) return

      const currentBase = isVariableLayer(currentBaseLayer) ? currentBaseLayer.variable : null
      const keyChanged = currentBase?.key !== variable.key
      const presetKeyChanged = currentBase?.presetKey !== variable.presetKey
      if (keyChanged || presetKeyChanged) {
        setFilters([])
        filterRef.current = []
      }

      setCurrentBaseLayer(current =>
        recommendedLayerForVariable(
          variable,
          hasAnyUserRole(context.user, ['systemAdministrator', 'administrator', 'manager']),
          stateId,
          stateName,
          current,
          preferredGeneralSettings
            ? {
                delimitationType: preferredGeneralSettings.preferredSpaceDelimitationType,
                delimitationId: preferredGeneralSettings.preferredSpaceDelimitationId,
                delimitationTitle: preferredGeneralSettings.preferredSpaceDelimitationTitle,
                childrenType: preferredGeneralSettings.preferredSpaceChildrenType,
              }
            : null,
        ),
      )

      const filterableBy = getFilterableByShape(variable.filterableBy)
      setFilterableBy(filterableBy)
    },
    [context.user, currentBaseLayer, filterRef, preferredGeneralSettings, stateId, stateName],
  )

  const handleNavigation = useCallback(() => {
    if (!('path' in currentBaseLayer.variable)) {
      return false
    }

    const path = currentBaseLayer.variable.path
    if (!path) {
      return false
    }

    if (location.pathname !== path) {
      navigate(path, {
        replace: true,
        state: {
          currentBaseLayer,
        },
      })
    }

    return true
  }, [currentBaseLayer, location.pathname, navigate])

  const handleCancel = () => {
    onClose()
    setCurrentBaseLayer(layer)
    setCollapsedStates({})
    setSearch('')
  }

  const nativeFilterOriginal = useRef<Expression | undefined>(
    isVariableLayer(currentBaseLayer) ? currentBaseLayer.variable.filter : undefined,
  )

  useEffect(() => {
    nativeFilterOriginal.current = isVariableLayer(currentBaseLayer)
      ? currentBaseLayer.variable.filter
      : undefined
  }, [currentBaseLayer])

  const handleConfirm = () => {
    if (handleNavigation()) {
      onClose()
      return
    }

    if (!isVariableLayer(currentBaseLayer)) {
      onClose()
      return
    }

    const expressions = filterRef?.current || []
    const nativeFilters = nativeFilterOriginal.current

    const validFilters = expressions.filter(
      filter => filter?.kind === 'binary' && filter?.right && Object.keys(filter?.right).length > 0,
    )

    // Group '=' filters by property so that filters on the same property are OR'ed together
    const equalityGroups: Record<string, Expression[]> = {}
    const remainingFilters: Expression[] = []

    validFilters.forEach(filter => {
      if (
        filter.kind === 'binary' &&
        filter.operator === '=' &&
        isPropertyExpression(filter.left)
      ) {
        const propertyKey = filter.left.property
        if (!equalityGroups[propertyKey]) {
          equalityGroups[propertyKey] = []
        }
        equalityGroups[propertyKey].push(filter)
      } else {
        remainingFilters.push(filter)
      }
    })

    // Build OR expressions for equality groups that have more than one filter
    const processedFilters: Expression[] = [...remainingFilters]
    Object.values(equalityGroups).forEach(group => {
      const combinedGroup = combineExpressions(group, 'OR')
      if (combinedGroup) {
        processedFilters.push(combinedGroup)
      }
    })

    // Finally, interleave the processed filters with AND
    const interleavedFilters = combineExpressions(processedFilters, 'AND')

    let finalFilter: Expression | undefined
    if (validFilters.length == 0) {
      finalFilter = nativeFilters
    } else if (nativeFilters && interleavedFilters) {
      finalFilter = {
        type: 'expression',
        kind: 'binary',
        left: nativeFilters,
        operator: 'AND',
        right: interleavedFilters,
      }
    } else {
      finalFilter = interleavedFilters
    }

    const withFilter = {
      ...currentBaseLayer,
      variable: {
        ...currentBaseLayer.variable,
        filter: finalFilter,
      },
    }
    setFilters(validFilters)
    onChangeLayer(withFilter)
    onClose()
  }

  const getCollapsedOpen = (isOpen: boolean, key: string | undefined) => {
    if (key) {
      const newState = { [key]: isOpen }
      setCollapsedStates(newState)
    }
  }

  const categoriesInfo = useMemo(
    () =>
      Object.entries(categories).map(([key, value]) => {
        return {
          key,
          title: value.title,
          sectorKey: value.sectorKey,
        }
      }),
    [],
  )

  const handleClick = (info: { key: string; title: string; sectorKey: string }) => {
    const currentCategoryKey = Object.keys(currentCategory)[0]
    const shouldReloadVariables =
      info.key !== currentCategoryKey ||
      search !== '' ||
      debouncedSearch !== '' ||
      indexes.startIndex !== 0 ||
      indexes.endIndex !== 50

    if (!shouldReloadVariables) {
      setCollapsedStates({})
      return
    }

    setVariablesPaginated([])
    setCollapsedStates({})

    setSearch('')
    setDebouncedSearch('')

    setHasChangedVariableLayer(true)

    setCurrentCategory({
      [info.key]: {
        title: info.title,
        sectorKey: info.sectorKey,
      },
    })
    setIndexes({ startIndex: 0, endIndex: 50 })
  }

  const searchQuestion = search.trim().length > 0 ? search : debouncedSearch
  const hasSearchQuery = searchQuestion.trim().length > 0
  const bestMatchScore = useMemo(() => {
    if (!hasSearchQuery || variablesPaginated.length === 0) {
      return null
    }

    const score = variablesPaginated.reduce((bestScore, variable) => {
      return Math.max(bestScore, variable.matchScore ?? 0)
    }, 0)

    return Number.isFinite(score) && score > 0 ? score : null
  }, [hasSearchQuery, variablesPaginated])
  const handleAskPrixAi = useCallback(
    async (question: string) => {
      await askPrixAi(buildVariableSearchQuestion(question))
    },
    [askPrixAi],
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} baseWidth='925px'>
      <WrapperModalVariable $flexDirection='row' $height='72vh' $justifyLeftContent='space-around'>
        {!isMobile && (
          <WrapperFilters
            categoriesInfo={categoriesInfo}
            currentCategory={currentCategory}
            handleClick={handleClick}
            setIsFilterOpen={setIsFilterOpen}
            isMobile={isMobile}
            isModalOpen={isOpen}
            variables={variables}
          />
        )}
        <div className='content'>
          <div className='filters'>
            <div className='search'>
              <input
                type='search'
                placeholder='Buscar ou perguntar para a Prix IA...'
                onChange={e => setSearch(e.target.value)}
                value={search}
              />
            </div>
            <div className='filter-category'>
              <ButtonFilter onClick={() => setIsFilterOpen(!isFilterOpen)}>
                <FontAwesomeIcon icon={faList} />
                {isFilterOpen && (
                  <Modal
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                  >
                    <WrapperModalVariable
                      $flexDirection='row'
                      $height='72vh'
                      $justifyLeftContent='space-around'
                    >
                      <WrapperFilters
                        categoriesInfo={categoriesInfo}
                        currentCategory={currentCategory}
                        handleClick={handleClick}
                        setIsFilterOpen={setIsFilterOpen}
                        isMobile={isMobile}
                        isModalOpen={isOpen}
                      />
                    </WrapperModalVariable>
                  </Modal>
                )}
              </ButtonFilter>
            </div>
          </div>
          <Handle
            isLoading={
              isDebouncing ||
              (filteredVariablesResult.isLoading && !variablesPaginated.length && !isLoadingMore) ||
              hasChangedVariableLayer
            }
          >
            {bestMatchScore !== null && (
              <VariableSearchMatchBanner
                bestMatchScore={bestMatchScore}
                question={searchQuestion}
                onAsk={handleAskPrixAi}
                isLoading={isOpeningPrixAi}
              />
            )}
            <Variables>
              {!filteredVariablesResult.isLoading && variablesPaginated.length === 0 ? (
                <NotFoundInfo>
                  {hasSearchQuery ? (
                    <AskPrixAiEmptyState
                      question={searchQuestion}
                      onAsk={handleAskPrixAi}
                      isLoading={isOpeningPrixAi}
                    />
                  ) : (
                    <p>Sem resultados encontrados</p>
                  )}
                </NotFoundInfo>
              ) : (
                <InfiniteScroll
                  variables={variablesPaginated}
                  currentBaseLayer={currentBaseLayer}
                  handleSelectVariable={handleSelectVariable}
                  getCollapsedOpen={getCollapsedOpen}
                  collapsedStates={collapsedStates}
                  setIndexes={setIndexes}
                  nextPages={filteredVariablesResult.result?.pagesLeft || 0}
                  apiIsLoading={filteredVariablesResult.isLoading}
                  setIsLoadingMore={setIsLoadingMore}
                  selectedVariableIsFilterableBy={filterableBy}
                  getFilter={getFilter}
                  filters={filters}
                />
              )}
            </Variables>
            <div
              style={{
                display: 'flex',
                padding: '10px',
                height: '50px',
                justifyContent: 'flex-end',
              }}
            >
              <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleCancel}>
                Cancelar
              </ButtonLinkAsSimpleText>
              <ButtonLink onGo={handleConfirm}>{hasPath ? 'Abrir' : 'Confirmar'}</ButtonLink>
            </div>
          </Handle>
        </div>
      </WrapperModalVariable>
    </Modal>
  )
}
